import { useTranslation } from "react-i18next";
import { TableColumn } from "react-data-table-component";
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Container, Row, Col, Card, CardBody, Button } from "reactstrap";
import { withRouter, useHistory, useParams } from "react-router-dom";
import { DebounceInput } from "react-debounce-input";
import {
  ColumnHeader,
  TextColumn,
  DateColumn,
  ActionButton,
  AppTable,
} from "../../../components";
import DeleteModal from "../../../components/Common/DeleteModal";
import { ActionTypeEnum, RoleEnum } from "../../../enums";
import { SubjectNoteDto } from "../../../interfaces";
import { PaginateQuery } from "../../../models";
import { AddEditSubjectNoteModal } from ".";
import {
  useGetSubjectNotesQuery,
  useDeleteSubjectNoteMutation,
  useToggleSubjectNoteMutation,
} from "../../../store/api/subjectNoteApi";
import { AccountState } from "../../../store/slices/accountSlice";

const SubjectNotes = () => {
  //#region hooks
  const { subjectId } = useParams<any>();
  const history = useHistory();
  const { t, i18n } = useTranslation();
  //#endregion

  //#region redux state
  const profile = useSelector(
    (state: { account: AccountState }) => state.account.userProfile,
  );

  //#endregion

  //#region internal state
  const initState = new PaginateQuery(10, "sequence_number:ASC");

  const [paginateQuery, setPaginateQuery] = useState<PaginateQuery>({
    ...initState,
    filter: {
      ["subjectId"]: subjectId,
    },
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [deletingItemId, setDeletingItemId] = useState<string | null>(null);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);

  //#endregion

  document.title = `${t("documentTitle", { pageName: "subjectNotes" })}`;

  const {
    data: response,
    isLoading: loading,
    refetch,
  } = useGetSubjectNotesQuery(paginateQuery);

  const [deleteSubjectNote, { isLoading: deleteSubjectNoteLoading }] =
    useDeleteSubjectNoteMutation();

  const [toggleSubjectNote] = useToggleSubjectNoteMutation();

  const columns: TableColumn<SubjectNoteDto>[] = [
    {
      name: <ColumnHeader title="sequence" />,
      cell: (row) => <TextColumn text={row.sequenceNumber + ""} />,
      sortable: true,
    },
    {
      name: <ColumnHeader title="name" />,
      cell: (row) => (
        <TextColumn text={row.name[i18n.language === "ar" ? "ar" : "en"]} />
      ),
      sortable: true,
    },
    {
      name: <ColumnHeader title="category" />,
      cell: (row) => (
        <TextColumn
          text={row.category?.name[i18n.language === "ar" ? "ar" : "en"] || "-"}
        />
      ),
      sortable: false,
    },
    {
      name: <ColumnHeader title="createdAt" />,
      cell: (row) => <DateColumn date={row.createdAt} />,
      sortable: true,
    },
    {
      name: <ColumnHeader title="action" />,
      sortable: false,
      cell: (row) => (
        <React.Fragment>
          <ActionButton
            onAction={() => onEdit(row.id)}
            actionType={ActionTypeEnum.EDIT}
          />
          &nbsp;
          {profile && profile.role.key === RoleEnum.SUPER_ADMIN && (
            <>
              <ActionButton
                onAction={() => onToggleState(row)}
                actionType={ActionTypeEnum.ACTIVE_INACTIVE}
                state={row.isActive}
              />
              &nbsp;
            </>
          )}
          <ActionButton
            onAction={() => onDelete(row.id)}
            actionType={ActionTypeEnum.DELETE}
          />
        </React.Fragment>
      ),
    },
  ];

  const getSortByColumnName = (name: string) => {
    let sortBy;
    switch (name) {
      case "sequence_number":
        sortBy = 1;
        break;
      case "name.en":
      case "name.ar":
        sortBy = 2;
        break;
      case "created_at":
        sortBy = 4;
        break;
      default:
        sortBy = 1;
        break;
    }
    return sortBy;
  };

  const [defaultSortFieldId, setDefaultSortFieldId] = useState(
    getSortByColumnName("sequence_number:ASC"),
  );
  const [defaultSortAsc, setDefaultSortAsc] = useState(false);
  const [defaultPage, setDefaultPage] = useState(1);

  //#region effects

  useEffect(() => {
    const queryParams = new URLSearchParams(history.location.search);
    let query: PaginateQuery = {
      limit: queryParams.has("limit")
        ? Number(queryParams.get("limit"))
        : paginateQuery.limit,
      page: queryParams.has("page")
        ? Number(queryParams.get("page"))
        : paginateQuery.page,
      sortBy: queryParams.has("sortBy")
        ? (queryParams.get("sortBy") as string)
        : paginateQuery.sortBy,
      search: queryParams.has("search")
        ? (queryParams.get("search") as string)
        : paginateQuery.search,
    };
    if (
      queryParams.get("sortBy") &&
      queryParams.get("sortBy")?.split(":").length &&
      queryParams.get("sortBy")?.split(":").length == 2
    ) {
      setDefaultSortAsc(
        (queryParams.get("sortBy")?.split(":")[1] as string) === "ASC",
      );
      setDefaultSortFieldId(
        getSortByColumnName(queryParams.get("sortBy")?.split(":")[0] as string),
      );
    } else {
      setDefaultSortAsc(false);
      setDefaultSortFieldId(2);
    }
    setDefaultPage(query.page);
    setPaginateQuery({ ...paginateQuery, ...query });
  }, []);

  useEffect(() => {
    if (paginateQuery !== initState) {
      const { limit, page, search, sortBy } = paginateQuery;
      let query;
      if (page) query = `?page=${page}`;
      if (limit) query += `&limit=${limit}`;
      if (search && search !== "") {
        history.replace({
          search: `&search=${search}`,
        });
      }
      if (sortBy) query += `&sortBy=${sortBy}`;
      if (search) query += `&search=${search}`;
      history.replace({ search: query });
    }
  }, [paginateQuery]);

  //#endregion

  //#region handlers

  const onAdd = () => {
    setShowAddEditModal(true);
  };

  const onEdit = (id: string) => {
    setEditingItemId(id);
    setShowAddEditModal(true);
  };

  const onToggleState = (row: SubjectNoteDto) => {
    toggleSubjectNote(row.id);
  };

  const onDelete = (id: string) => {
    setDeletingItemId(id);
    setShowDeleteModal(true);
  };

  const onDeleteConfirm = async () => {
    try {
      await deleteSubjectNote(deletingItemId as string).unwrap();
      setDeletingItemId(null);
      setShowDeleteModal(false);
    } catch (err) {
      console.error(err);
    }
  };

  const onSearch = (searchText: string) => {
    setPaginateQuery({ ...paginateQuery, search: searchText });
  };

  const getSortByColumnId = (columnId: number, sortDirection: string) => {
    let sortBy;
    switch (columnId) {
      case 1:
        sortBy = "sequence_number";
        break;
      case 2:
        sortBy = "name" + "." + i18n.language;
        break;
      case 4:
        sortBy = "created_at";
        break;
      default:
        sortBy = "sequence_number";
        break;
    }
    return `${sortBy}:${sortDirection.toUpperCase()}`;
  };

  const onChangePage = (page: number) => {
    setPaginateQuery({ ...paginateQuery, page: page });
  };

  const onChangeRowsPerPage = (
    currentRowsPerPage: number,
    currentPage: number,
  ) => {
    setPaginateQuery({
      ...paginateQuery,
      limit: currentRowsPerPage,
      page: currentPage,
    });
  };

  const onSort = (
    column: TableColumn<SubjectNoteDto>,
    sortDirection: string,
  ) => {
    const sortBy = getSortByColumnId(column.id as number, sortDirection);
    setPaginateQuery({
      ...paginateQuery,
      sortBy: sortBy as string,
    });
  };

  //#endregion

  return (
    <React.Fragment>
      <Container fluid>
        <Row className="mt-2 mb-4 align-items-center">
          <Col className="col-sm-auto">
            <div>
              <h4 className="mb-0">{t("notes")}</h4>
            </div>
          </Col>
          <Col className="col-sm">
            <div className="d-flex justify-content-sm-end gap-2">
              <Button
                color="success"
                onClick={() => {
                  setPaginateQuery({ ...paginateQuery });
                  refetch();
                }}
              >
                {t("refresh")}
              </Button>
            </div>
          </Col>
        </Row>
        <Row>
          <Col xxl={12}>
            <Card>
              <CardBody>
                <Row className="g-2">
                  <Col sm={3} className="mb-2">
                    <div className="search-box">
                      <div className="search-box">
                        <DebounceInput
                          className="form-control"
                          placeholder={`${t("searchPlaceholder")}...`}
                          minLength={2}
                          debounceTimeout={300}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                          ) => onSearch(event.target.value)}
                          value={paginateQuery.search}
                        />
                        <i className="ri-search-line search-icon"></i>
                      </div>
                    </div>
                  </Col>
                  <Col className="col-sm-auto ms-auto">
                    <div className="list-grid-nav hstack gap-1">
                      <Button color="success" onClick={() => onAdd()}>
                        <i className="ri-add-fill me-1 align-bottom"></i>
                        {t("addNote")}
                      </Button>
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col sm={12}>
                    <AppTable
                      columns={columns}
                      data={response?.data || []}
                      progressPending={loading}
                      onSort={onSort}
                      onChangeRowsPerPage={onChangeRowsPerPage}
                      onChangePage={onChangePage}
                      defaultSortFieldId={defaultSortFieldId}
                      paginationDefaultPage={defaultPage}
                      defaultSortAsc={defaultSortAsc}
                      paginationTotalRows={response?.meta?.totalItems}
                    />
                  </Col>
                </Row>
                <DeleteModal
                  loading={deleteSubjectNoteLoading}
                  show={showDeleteModal}
                  onDeleteClick={() => onDeleteConfirm()}
                  onCloseClick={() => {
                    setShowDeleteModal(false);
                  }}
                />
                <AddEditSubjectNoteModal
                  subjectId={subjectId}
                  id={editingItemId}
                  show={showAddEditModal}
                  onCloseClick={() => {
                    setShowAddEditModal(false);
                    setEditingItemId(null);
                  }}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </React.Fragment>
  );
};

export const SubjectNotesPage = withRouter(SubjectNotes);
